import { computed, reactive, ref } from 'vue'
import { getElementIdsReference } from '@/utils/filterListByString'
import {
  searchChoices,
  searchComponents,
  searchOptions,
  searchComponentVariant
} from '@/api'
import { PM_TYPE, SCHEDULE_TYPES, SEARCH_RESULT_TYPES } from '@/constants'

export const useElements = () => {
  const isElementsLoading = ref(false)
  const selectedForLE = ref(false)

  const elements = reactive({
    options: { elements: [], totalSize: 0 },
    components: { elements: [], totalSize: 0 },
    componentVariants: { elements: [], totalSize: 0 },
    choices: { elements: [], totalSize: 0 }
  })

  const selectedElementsIds = reactive({
    [SEARCH_RESULT_TYPES.OPTIONS]: new Map(),
    [SEARCH_RESULT_TYPES.COMPONENTS]: new Map()
  })

  const selectedElements = reactive({
    [SEARCH_RESULT_TYPES.OPTIONS]: [],
    [SEARCH_RESULT_TYPES.COMPONENTS]: []
  })

  const onHandleParentElementClick = (elementTypes, {
    item = {},
    isChosen = false,
    fetchedElements = null
  } = {}) => {
    const { item: parentType, subItem: childType } = elementTypes
    // eslint-disable-next-line security/detect-object-injection
    const selectedIds = selectedElementsIds[parentType] || new Map()

    if (isChosen) {
      selectedIds.delete(item.id)
      // eslint-disable-next-line security/detect-object-injection
      selectedElements[parentType] = selectedElements[parentType]?.filter(el => el.id !== item.id) || []
      // eslint-disable-next-line security/detect-object-injection
      selectedElementsIds[parentType] = selectedIds
      return
    }

    const { elements: parentElements } = fetchedElements
      // eslint-disable-next-line security/detect-object-injection
      ? { elements: fetchedElements[parentType] } : elements[parentType]
    // eslint-disable-next-line security/detect-object-injection
    const { elements: subElements } = elements[childType]
    const { elementId } = getElementIdsReference(childType)

    const getSubElementById = () => {
      // eslint-disable-next-line security/detect-object-injection
      const subElement = subElements.find(el => el[elementId] === item.id)
      return subElement ? item : []
    }

    const getElementById = () => parentElements.find(el => el.id === item.id)

    const selectedItem = getElementById() || getSubElementById()

    const subElementIds = selectedItem
      // eslint-disable-next-line security/detect-object-injection
      ? selectedItem[childType]?.map(subEl => subEl.id) || []
      // eslint-disable-next-line security/detect-object-injection
      : subElements.filter(el => el[elementId] === item.id).map(el => el.id)

    selectedIds.set(item.id, subElementIds)

    // eslint-disable-next-line security/detect-object-injection
    const updatedElements = selectedElements[parentType]?.filter(el => el.id === item.id) || []

    if (updatedElements.length) {
      // eslint-disable-next-line security/detect-object-injection
      selectedElements[parentType] = selectedElements[parentType]
        .map(el => (el.id === selectedItem.id ? { ...el, ...selectedItem } : el))
    }
    else {
      // eslint-disable-next-line security/detect-object-injection
      selectedElements[parentType] = [...(selectedElements[parentType] || []), selectedItem]
    }

    // eslint-disable-next-line security/detect-object-injection
    selectedElementsIds[parentType] = selectedIds
  }

  const onHandleChildElementClick = (elementTypes, { subItem = {}, isChosen = false } = {}) => {
    const elementSingularTypeName = elementTypes.item.slice(0, -1)
    const parentId = `${ elementSingularTypeName }Id`

    const parentElementIdsMap = selectedElementsIds[elementTypes.item] || new Map()
    let parentElement = selectedElements[elementTypes.item]
    // eslint-disable-next-line security/detect-object-injection
    const subItemIdList = parentElementIdsMap.get(subItem[parentId]) || []

    const { elementId } = getElementIdsReference(elementTypes.subItem)

    const handleChosenItem = () => {
      const targetSubItemIdx = subItemIdList.indexOf(subItem.id)

      if (targetSubItemIdx > -1) {
        if (subItemIdList.length > 1) {
          subItemIdList.splice(targetSubItemIdx, 1)
          // eslint-disable-next-line security/detect-object-injection
          parentElementIdsMap.set(subItem[parentId], subItemIdList)

          parentElement = parentElement.map(el => {
            const subItems = el[elementTypes.subItem].filter(subEl => subEl.id !== subItem.id)
            return { ...el, [elementTypes.subItem]: subItems }
          })
        }
        else {
          // eslint-disable-next-line security/detect-object-injection
          parentElementIdsMap.delete(subItem[parentId])
          // eslint-disable-next-line security/detect-object-injection
          parentElement = parentElement.filter(el => el.id !== subItem[parentId])
        }
        selectedElementsIds[elementTypes.item] = new Map(parentElementIdsMap)
        selectedElements[elementTypes.item] = parentElement
      }
    }

    const handleUnchosenItem = () => {
      // eslint-disable-next-line security/detect-object-injection
      if (parentElementIdsMap.has(subItem[parentId]) && !subItemIdList.includes(subItem.id)) {
        subItemIdList.push(subItem.id)
        // eslint-disable-next-line security/detect-object-injection
        parentElementIdsMap.set(subItem[parentId], subItemIdList)

        parentElement = parentElement.map(el => {
          // eslint-disable-next-line security/detect-object-injection
          if (el.id === subItem[elementId]) {
            return { ...el, [elementTypes.subItem]: [...el[elementTypes.subItem], subItem] }
          }
          return el
        })
        selectedElements[elementTypes.item] = parentElement
      }
      else {
        const element = {
          // eslint-disable-next-line security/detect-object-injection
          id: subItem[parentId],
          id2: subItem[`${ elementSingularTypeName }Id2`],
          oid: subItem[`${ elementSingularTypeName }Oid`],
          name: subItem[`${ elementSingularTypeName }Name`],
          [elementTypes.subItem]: [{ ...subItem }]
        }
        parentElementIdsMap.set(element.id, [subItem.id])
        parentElement.push(element)
      }
      selectedElementsIds[elementTypes.item] = new Map(parentElementIdsMap)
      selectedElements[elementTypes.item] = parentElement
    }

    isChosen ? handleChosenItem() : handleUnchosenItem()
  }

  const onClearCollection = elementsType => {
    // eslint-disable-next-line security/detect-object-injection
    selectedElements[elementsType] = []
    // eslint-disable-next-line security/detect-object-injection
    selectedElementsIds[elementsType] = new Map()
  }

  const totalElements = computed(() => SCHEDULE_TYPES.reduce((acc, type) => {
    // eslint-disable-next-line security/detect-object-injection
    acc[type] = elements[type]?.totalSize || 0
    return acc
  }, {}))

  const fetchElements = async ({ query, pmIdentifier, pmType = PM_TYPE.PRODUCT_MODEL }) => {
    isElementsLoading.value = true
    try {
      const params = { query, pmIdentifier, pmType }

      const data = await Promise.all([
        searchOptions(params),
        searchChoices(params),
        searchComponents(params),
        searchComponentVariant(params)
      ])

      SCHEDULE_TYPES.forEach((key, index) => {
        // eslint-disable-next-line security/detect-object-injection
        elements[key] = data[index]
      })
    }
    catch (err) {
      throw new Error('Failed to fetch the elements', err)
    }
    finally {
      isElementsLoading.value = false
    }
  }

  const selectedComponentsWithLEs = () => selectedElements[SEARCH_RESULT_TYPES.COMPONENTS]
    .map(item => ({ ...item, selectedForLE: selectedForLE.value }))

  const setSelectedForLE = status => {
    selectedForLE.value = status
  }

  return {
    elements,
    fetchElements,
    loading: isElementsLoading,
    totalElements,
    onHandleParentElementClick,
    onHandleChildElementClick,
    onClearCollection,
    selectedElementsIds,
    selectedElements,
    selectedForLE,
    setSelectedForLE,
    selectedComponentsWithLEs
  }
}
